// src/components/Dashboard/RoleDashboard/RoleExtras/Feed.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import YouTube from "react-youtube";
import { apiFetch } from "../../../../apiFetch";
import { useLocation, useNavigate } from "react-router-dom";
import "./Feed.css";

const NEW_WINDOW_MS = 1000 * 60 * 60 * 24 * 7; // 7 days

const Feed = () => {
    const navigate = useNavigate();
    const location = useLocation();

    // ✅ derive viewer from navigation state OR localStorage fallback
    const stateViewerRole = location.state?.viewerRole;
    const storedRole = (
        localStorage.getItem("userRole") || localStorage.getItem("role") || ""
    ).toLowerCase();

    const viewerRole = (stateViewerRole || storedRole || "").toLowerCase();

    let viewerId = location.state?.viewerId || null;
    if (!viewerId && viewerRole) {
        const idKeyMap = {
            player: "playerId",
            agent: "agentId",
            scout: "scoutId",
            club: "clubId",
        };
        const key = idKeyMap[viewerRole] || "userId";
        viewerId = localStorage.getItem(key);
    }

    const [videos, setVideos] = useState([]);
    const [userRatings, setUserRatings] = useState({}); // ⭐ per-viewer ratings (by youtube id)
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [reportedVideos, setReportedVideos] = useState({});

    const [reportFeedback, setReportFeedback] = useState({
        videoId: null,
        text: "",
    });

    // UI polish state
    const [sortMode, setSortMode] = useState("newest"); // newest | oldest
    const [search, setSearch] = useState("");

    // ✅ NEW: include watched toggle (controls backend 90% hide rule)
    const [includeWatched, setIncludeWatched] = useState(false);

    // video open state + tracking (copied from VideoProfile patterns)
    const [openVideoId, setOpenVideoId] = useState(null);
    const watchIntervalRef = useRef(null);
    const markSurfacedTimeoutRef = useRef(null);
    const lastClickedVideoRef = useRef(null);
    const clientExpectedRef = useRef(0);

    const activeSessionIdRef = useRef(null);
    const eventCounterRef = useRef(0);

    // ✅ NEW: keep current open video in a ref (prevents stale state in callbacks)
    const openVideoIdRef = useRef(null);
    useEffect(() => {
        openVideoIdRef.current = openVideoId;
    }, [openVideoId]);

    // ✅ YouTube player refs for accurate tracking
    const ytPlayersRef = useRef({}); // videoId -> player
    const lastPlaybackTimeRef = useRef(0);
    const isPlayingRef = useRef(false);
    const activeVideoIdRef = useRef(null);




    const makeClientId = () => {
        if (typeof crypto !== "undefined" && crypto.randomUUID) {
            return crypto.randomUUID();
        }

        return `id-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    };

    const beginWatchSession = (videoId) => {
        activeVideoIdRef.current = String(videoId);
        activeSessionIdRef.current = makeClientId();
        eventCounterRef.current = 0;
    };

    const nextEventId = () => {
        eventCounterRef.current += 1;

        const sessionId = activeSessionIdRef.current || "no-session";
        return `${sessionId}-${eventCounterRef.current}`;
    };





    const stopWatchTracking = ({ resetSession = true } = {}) => {
        if (watchIntervalRef.current) {
            clearInterval(watchIntervalRef.current);
            watchIntervalRef.current = null;
        }

        clientExpectedRef.current = 0;
        lastPlaybackTimeRef.current = 0;
        isPlayingRef.current = false;
        activeVideoIdRef.current = null;

        if (resetSession) {
            activeSessionIdRef.current = null;
            eventCounterRef.current = 0;
        }
    };



    const flushActiveWatchProgress = async () => {
        try {
            if (!viewerId || !viewerRole) return;
            if (!isPlayingRef.current) return;

            const videoId = activeVideoIdRef.current;
            if (!videoId) return;

            const player = ytPlayersRef.current[String(videoId)];
            if (!player?.getCurrentTime) return;

            let now = 0;
            try {
                now = await player.getCurrentTime();
            } catch {
                return;
            }

            const prev = Number(lastPlaybackTimeRef.current || 0);
            let delta = now - prev;

            if (!Number.isFinite(delta) || delta <= 0) {
                lastPlaybackTimeRef.current = now;
                return;
            }

            if (delta > 15) {
                lastPlaybackTimeRef.current = now;
                return;
            }

            lastPlaybackTimeRef.current = now;

            if (!activeSessionIdRef.current) {
                beginWatchSession(videoId);
            }

            const openVideo = videos.find(
                (v) => String(v.youtube_video_id) === String(videoId)
            );

            const res = await apiFetch("/api/videos/watch-progress", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    viewerId,
                    viewerRole,
                    videoId,
                    watchTimeDelta: delta,
                    videoDuration: openVideo?.durationSeconds || 300,
                    sessionId: activeSessionIdRef.current,
                    eventId: nextEventId(),
                    clientNow: new Date().toISOString(),
                    clientExpectedWatchTimeSeconds: clientExpectedRef.current + delta,
                }),
            });

            if (!res.ok) return;

            const data = await res.json().catch(() => ({}));
            clientExpectedRef.current += delta;

            if (data?.alreadyCompleted || data?.engagementLevel === "fully_watched") {
                stopWatchTracking();
            }
        } catch {
            // ignore flush errors
        }
    };




    const startWatchTracking = (videoId, videoDuration) => {
        if (!viewerId || !viewerRole) return;

        if (watchIntervalRef.current) stopWatchTracking();

        beginWatchSession(videoId);

        // keep authoritative active video + duration
        activeVideoIdRef.current = String(videoId);

        // baseline from actual playback position (if ready)
        const player = ytPlayersRef.current[String(videoId)];
        try {
            const t = player?.getCurrentTime?.();
            lastPlaybackTimeRef.current = Number.isFinite(t) ? t : 0;
        } catch {
            lastPlaybackTimeRef.current = 0;
        }

        watchIntervalRef.current = setInterval(async () => {
            // only count when tab visible + actually playing
            if (document.visibilityState !== "visible") return;
            if (!isPlayingRef.current) return;

            const player = ytPlayersRef.current[String(videoId)];
            if (!player?.getCurrentTime) return;

            // ensure we’re still tracking the same video
            if (activeVideoIdRef.current !== String(videoId)) return;

            let now = 0;
            try {
                now = await player.getCurrentTime();
            } catch {
                return;
            }

            const prev = Number(lastPlaybackTimeRef.current || 0);
            let delta = now - prev;

            // no progress / seek backwards / invalid
            if (!Number.isFinite(delta) || delta <= 0) {
                lastPlaybackTimeRef.current = now;
                return;
            }

            // guard against huge jumps (sleep/tab throttle)
            if (delta > 15) {
                lastPlaybackTimeRef.current = now;
                return;
            }

            // update baseline before sending
            lastPlaybackTimeRef.current = now;

            if (!activeSessionIdRef.current) {
                beginWatchSession(videoId);
            }

            try {
                const res = await apiFetch("/api/videos/watch-progress", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        viewerId,
                        viewerRole,
                        videoId,
                        watchTimeDelta: delta,
                        videoDuration: videoDuration || 300,
                        sessionId: activeSessionIdRef.current,
                        eventId: nextEventId(),
                        clientNow: new Date().toISOString(),
                        clientExpectedWatchTimeSeconds: clientExpectedRef.current + delta,
                    }),
                });

                if (!res.ok) {
                    const text = await res.text().catch(() => "");
                    console.warn("watch-progress rejected:", res.status, text);
                    return;
                }

                const data = await res.json();

                clientExpectedRef.current += delta;
                const serverWatch = Number(data?.clampedWatchTimeSeconds);
                const diff = Math.abs(clientExpectedRef.current - serverWatch);

                if (Number.isFinite(serverWatch)) {
                    if (diff <= 2) {
                        console.log("✅ MATCH", {
                            videoId,
                            clientExpected: clientExpectedRef.current,
                            serverWatch,
                            diff,
                        });
                    } else {
                        console.warn("❌ DRIFT", {
                            videoId,
                            clientExpected: clientExpectedRef.current,
                            serverWatch,
                            diff,
                            data,
                        });
                    }
                }

                if (data?.alreadyCompleted || data?.engagementLevel === "fully_watched") {
                    stopWatchTracking();
                }
            } catch {
                // ignore network errors
            }
        }, 5000);
    };




    const toDate = (v) => {
        const raw =
            v?.published_at ||
            v?.publishedAt ||
            v?.publishedAtMs ||
            v?.createdAt ||
            null;
        if (!raw) return null;
        const d = new Date(raw);
        return isNaN(d.getTime()) ? null : d;
    };

    const isNew = (v) => {
        const d = toDate(v);
        if (!d) return false;
        return Date.now() - d.getTime() <= NEW_WINDOW_MS;
    };

    const timeAgo = (d) => {
        if (!d) return "";
        const diff = Date.now() - d.getTime();
        const mins = Math.floor(diff / (1000 * 60));
        const hrs = Math.floor(diff / (1000 * 60 * 60));
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        if (mins < 60) return `${mins}m ago`;
        if (hrs < 24) return `${hrs}h ago`;
        return `${days}d ago`;
    };

    // ✅ Fetch subscription feed videos
    const fetchFeed = async () => {
        setLoading(true);
        setError(null);

        try {
            const queryParams = new URLSearchParams({
                viewerId: viewerId || "",
                viewerRole: viewerRole || "",
                includeWatched: includeWatched ? "true" : "false", // ✅ NEW
            }).toString();

            const res = await apiFetch(`/api/video-feed?${queryParams}`);

            if (!res.ok) {
                const text = await res.text().catch(() => "");
                throw new Error(`Feed failed: ${res.status} ${text}`);
            }

            const data = await res.json();
            const list = Array.isArray(data) ? data : data.videos || [];

            const enriched = list.map((video) => ({
                ...video,
                averageRating: video.averageRating ?? video.average_rating ?? null,
                viewerRating: video.viewerRating ?? video.viewer_rating ?? null,
            }));

            // hydrate ratings from viewerRating
            const initialRatings = {};
            enriched.forEach((v) => {
                if (v.viewerRating != null) {
                    initialRatings[String(v.youtube_video_id)] = v.viewerRating;
                }
            });

            setVideos(enriched);
            setUserRatings(initialRatings);

            // ✅ hydrate persistent reported state
            const initialReported = {};

            enriched.forEach((v) => {
                const id = String(v.youtube_video_id);

                if (v.viewerHasReported) {
                    initialReported[id] = true;
                }
            });

            setReportedVideos(initialReported);
        } catch (err) {
            console.error("❌ Feed fetch error:", err);
            setError("Failed to load feed videos.");
            setVideos([]);
        } finally {
            setLoading(false);
        }
    };



    useEffect(() => {
        if (!viewerRole || viewerRole === "player") return;

        fetchFeed();

        return () => {
            (async () => {
                await flushActiveWatchProgress();
                stopWatchTracking();
                if (markSurfacedTimeoutRef.current) {
                    clearTimeout(markSurfacedTimeoutRef.current);
                }
            })();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewerRole, viewerId, includeWatched]);




    const navigateToProfile = (playerSlug) => {
        if (!playerSlug) return;

        const role = (viewerRole || "agent").toLowerCase();

        navigate(`/${role}-dashboard/player-profile/${playerSlug}`, {
            state: {
                viewerRole: role,
                viewerId: viewerId || null,
                from: "feed",
            },
        });
    };




    const togglePlayer = async (videoId, videoDurationSeconds) => {
        await flushActiveWatchProgress();
        stopWatchTracking();

        if (markSurfacedTimeoutRef.current) {
            clearTimeout(markSurfacedTimeoutRef.current);
            markSurfacedTimeoutRef.current = null;
        }

        const isSameVideo = lastClickedVideoRef.current === videoId;
        lastClickedVideoRef.current = videoId;

        if (!isSameVideo && viewerId && viewerRole) {
            markSurfacedTimeoutRef.current = setTimeout(() => {
                apiFetch("/api/videos/mark-surfaced", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ viewerId, viewerRole, videoIds: [videoId] }),
                }).catch(() => { });
            }, 2000);
        }

        setOpenVideoId((prev) => {
            const next = prev === videoId ? null : videoId;

            if (!next) {
                stopWatchTracking();
            }

            return next;
        });
    };



    const showReportFeedback = (videoId, text) => {
        setReportFeedback({
            videoId: String(videoId),
            text,
        });

        if (showReportFeedback._t) {
            clearTimeout(showReportFeedback._t);
        }

        showReportFeedback._t = setTimeout(() => {
            setReportFeedback({
                videoId: null,
                text: "",
            });
        }, 2000);
    };

    const reportVideo = async (videoId) => {
        try {
            if (!viewerId || !viewerRole) {
                showReportFeedback(videoId, "Please sign in to report.");
                return;
            }

            await apiFetch("/api/report-video", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    videoId,
                    reporterId: viewerId,
                    reporterRole: viewerRole,
                }),
            });

            setReportedVideos((prev) => ({
                ...prev,
                [videoId]: true,
            }));

            showReportFeedback(
                videoId,
                "Report received. Thank you."
            );

        } catch (err) {
            console.error("Failed to report video", err);

            showReportFeedback(
                videoId,
                "Report failed. Try again."
            );
        }
    };




    const handleRate = async (videoId, ratingValue) => {
        if (!viewerId || viewerRole === "player") return;

        const normalizedId = String(videoId);

        setUserRatings((prev) => ({
            ...prev,
            [normalizedId]: ratingValue,
        }));

        try {
            const res = await apiFetch("/api/rate-video", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    videoId,
                    viewerId,
                    viewerRole,
                    rating: ratingValue,
                }),
            });

            if (!res.ok) {
                const text = await res.text().catch(() => "");
                throw new Error(`Rating failed: ${res.status} ${text}`);
            }
        } catch (err) {
            console.error("❌ Failed to submit rating:", err);
        }
    };

    const renderRating = (videoId) => {
        const canRate = !!viewerId && viewerRole !== "player";
        const key = String(videoId);
        const effectiveRating = userRatings[key] ?? 0;

        return (
            <div className="feed-rating-stars">
                {[1, 2, 3, 4, 5].map((value) => (
                    <span
                        key={value}
                        onClick={() => canRate && handleRate(videoId, value)}
                        className={value <= effectiveRating ? "filled" : ""}
                        role="button"
                        aria-label={`Rate ${value} star`}
                    >
                        ★
                    </span>
                ))}
            </div>
        );
    };

    const showEmpty = !loading && !error && videos.length === 0;

    const visibleVideos = useMemo(() => {
        const q = (search || "").trim().toLowerCase();

        let list = [...videos];

        // search by title
        if (q) {
            list = list.filter((v) => (v.title || "").toLowerCase().includes(q));
        }

        // sort
        list.sort((a, b) => {
            const da = toDate(a)?.getTime() ?? 0;
            const db = toDate(b)?.getTime() ?? 0;
            return sortMode === "oldest" ? da - db : db - da;
        });

        return list;
    }, [videos, search, sortMode]);

    return (
        <div className="feed-page">
            <div className="feed-container">
                <h1 className="feed-title">Your Feed</h1>

                <p className="feed-subtitle">
                    New videos from players you subscribe to will appear here.
                </p>

                {/* ✅ Controls */}
                {!error && (
                    <div className="feed-controls">
                        <div className="feed-search-wrap">
                            <input
                                className="feed-search-input"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search titles…"
                                aria-label="Search titles"
                            />
                        </div>

                        <div className="feed-sort-wrap">
                            <label className="feed-sort-label">Sort</label>
                            <select
                                className="feed-sort-select"
                                value={sortMode}
                                onChange={(e) => setSortMode(e.target.value)}
                                aria-label="Sort feed"
                            >
                                <option value="newest">Newest</option>
                                <option value="oldest">Oldest</option>
                            </select>
                        </div>

                        {/* ✅ Show watched toggle */}
                        <label
                            className="feed-watched-toggle"
                            title="Show videos you already watched (90%+)"
                        >
                            <input
                                type="checkbox"
                                checked={includeWatched}
                                onChange={(e) => setIncludeWatched(e.target.checked)}
                            />

                            <span>Include watched</span>
                        </label>



                        <button
                            className="feed-refresh-btn"
                            onClick={async () => {
                                await flushActiveWatchProgress();
                                stopWatchTracking();
                                fetchFeed();
                            }}
                            title="Refresh feed"
                            type="button"
                        >
                            Refresh
                        </button>
                    </div>
                )}

                {loading && (
                    <div className="feed-empty-card">
                        <div className="feed-empty-icon">⏳</div>
                        <h2>Loading feed…</h2>
                        <p>Please wait while we fetch your subscribed videos.</p>
                    </div>
                )}

                {error && !loading && (
                    <div className="feed-empty-card">
                        <div className="feed-empty-icon">⚠</div>
                        <h2>Could not load feed</h2>
                        <p>{error}</p>
                        <button
                            className="feed-retry-btn"
                            onClick={async () => {
                                await flushActiveWatchProgress();
                                stopWatchTracking();
                                fetchFeed();
                            }}
                            type="button"
                        >
                            Retry
                        </button>
                    </div>
                )}

                {showEmpty && (
                    <div className="feed-empty-card">
                        <div className="feed-empty-icon">▶</div>
                        <h2>No feed items yet</h2>
                        <p>
                            Subscribe to player profiles to start building your personalized
                            scouting feed.
                        </p>
                    </div>
                )}

                {!loading && !error && visibleVideos.length > 0 && (
                    <div className="feed-videos">
                        <div className="video-profile-scroll-container">
                            <div className="video-profile-grid">
                                {visibleVideos.map((video) => {
                                    const playerSlug =
                                        video.player?.profileSlug ||
                                        video.player?.slug ||
                                        video.playerProfileSlug ||
                                        null;

                                    const open = openVideoId === video.youtube_video_id;
                                    const publishedDate = toDate(video);
                                    const isVideoNew = isNew(video);

                                    return (
                                        <div
                                            key={video.youtube_video_id}
                                            className="feed-video-item-wrapper"
                                        >

                                            <div className={`video-card ${isVideoNew ? "is-new" : ""}`}>
                                                {reportFeedback.videoId === String(video.youtube_video_id) && (
                                                    <div className="feed-report-feedback">
                                                        {reportFeedback.text}
                                                    </div>
                                                )}
                                                {!open ? (
                                                    <div
                                                        className="thumbnail-container"
                                                        onClick={() =>
                                                            togglePlayer(
                                                                video.youtube_video_id,
                                                                video.durationSeconds
                                                            )
                                                        }
                                                    >
                                                        <img src={video.thumbnail_url} alt={video.title} />

                                                        {/* ✅ NEW badge */}
                                                        {isVideoNew && (
                                                            <div className="feed-badge-new">
                                                                NEW{" "}
                                                                <span className="feed-badge-sub">
                                                                    {timeAgo(publishedDate)}
                                                                </span>
                                                            </div>
                                                        )}

                                                        {/* ✅ date badge (even if not new) */}
                                                        {publishedDate && !isVideoNew && (
                                                            <div className="feed-badge-date">
                                                                {timeAgo(publishedDate)}
                                                            </div>
                                                        )}

                                                        <div className="play-button">▶</div>
                                                    </div>
                                                ) : (
                                                    <div className="video-wrapper">
                                                        <YouTube
                                                            videoId={video.youtube_video_id}
                                                            opts={{
                                                                width: "100%",
                                                                height: "100%",
                                                                playerVars: {
                                                                    autoplay: 1,
                                                                    modestbranding: 1,
                                                                    rel: 0,
                                                                },
                                                            }}
                                                            onReady={(e) => {
                                                                ytPlayersRef.current[String(video.youtube_video_id)] =
                                                                    e.target;

                                                                try {
                                                                    lastPlaybackTimeRef.current =
                                                                        e.target.getCurrentTime() || 0;
                                                                } catch {
                                                                    lastPlaybackTimeRef.current = 0;
                                                                }
                                                            }}
                                                            onStateChange={(e) => {
                                                                const isActive =
                                                                    String(openVideoIdRef.current) ===
                                                                    String(video.youtube_video_id);

                                                                if (!isActive) {
                                                                    isPlayingRef.current = false;
                                                                    return;
                                                                }

                                                                // 1 = PLAYING, 2 = PAUSED, 0 = ENDED
                                                                if (e.data === 1) {
                                                                    isPlayingRef.current = true;

                                                                    // ✅ reset baseline on resume so delta doesn't jump
                                                                    try {
                                                                        lastPlaybackTimeRef.current =
                                                                            e.target.getCurrentTime() ||
                                                                            lastPlaybackTimeRef.current ||
                                                                            0;
                                                                    } catch { }

                                                                    if (!watchIntervalRef.current) {
                                                                        startWatchTracking(
                                                                            video.youtube_video_id,
                                                                            video.durationSeconds || 300
                                                                        );
                                                                    }
                                                                } else if (e.data === 2) {
                                                                    // ⏸ PAUSED → flush progress
                                                                    (async () => {
                                                                        await flushActiveWatchProgress();
                                                                        isPlayingRef.current = false;
                                                                    })();
                                                                } else if (e.data === 0) {
                                                                    // ⏹ ENDED
                                                                    (async () => {
                                                                        await flushActiveWatchProgress();
                                                                        stopWatchTracking();
                                                                    })();
                                                                } else {
                                                                    isPlayingRef.current = false;
                                                                }
                                                            }}
                                                        />

                                                        <img
                                                            className={`avatar-overlay ${!playerSlug ? "disabled" : ""
                                                                }`}
                                                            src="/images/VSN.png"
                                                            alt="Visit Profile"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                if (playerSlug) navigateToProfile(playerSlug);
                                                            }}
                                                        />
                                                    </div>
                                                )}

                                                <h4 title={video.title}>
                                                    {video.title}
                                                </h4>

                                            </div>

                                            <div className="feed-video-actions-bar">

                                                <button
                                                    className="feed-vsn-profile-btn"
                                                    onClick={() => navigateToProfile(playerSlug)}
                                                    title="Click to open Player Profile"
                                                >
                                                    VSN
                                                </button>

                                                <div className="feed-video-scout-rating">
                                                    <span className="feed-rating-label">Scout Rating</span>
                                                    {renderRating(video.youtube_video_id)}
                                                </div>

                                                <button
                                                    className="feed-report-video-btn"
                                                    type="button"
                                                    onClick={() => reportVideo(video.youtube_video_id)}
                                                    disabled={reportedVideos[video.youtube_video_id]}
                                                >
                                                    {reportedVideos[video.youtube_video_id] ? "Reported" : "Report"}
                                                </button>

                                            </div>

                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Feed;





